import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useI18n } from '@nx/i18n';
import { PublicProfile } from '../../components/profile/PublicProfile';
import { getProfilePageBackdrop } from '../../components/profile/profilePageBackdrop';
import { toProfileViewModel } from '../../components/profile/profileViewModel';
import './profile-public-page.css';
import { useProfilePageMeta } from '../../hooks/useProfilePageMeta';
import { recordComponentClick } from '../../services/profileCallables';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { publicProfileLoadRequested } from '../../store/slices/publicProfileSlice';

function isNavigableUrl(url: string | undefined): boolean {
  return Boolean(url && url !== '#');
}

export function PublicProfilePage() {
  const { slug = '' } = useParams();
  const dispatch = useAppDispatch();
  const { t } = useI18n();
  const { profile, components, loading, notFound } = useAppSelector((s) => s.publicProfile);

  useEffect(() => {
    if (slug) dispatch(publicProfileLoadRequested(slug));
  }, [dispatch, slug]);

  useProfilePageMeta(
    profile
      ? {
          title: profile.title,
          description: profile.description,
          tagline: profile.tagline,
          avatarUrl: profile.avatarUrl,
          slug: profile.slug,
        }
      : null,
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#d9d2c7]">
        <Loader2 className="h-8 w-8 animate-spin text-[#78665A]" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FAF8F5] px-4">
        <p className="text-lg text-[#3D3328]">{t('profile.public.notFound')}</p>
        <Link to="/discover" className="text-sm font-semibold text-[#1A1A1A] underline">
          {t('profile.discover.eyebrow')}
        </Link>
      </div>
    );
  }

  const viewModel = toProfileViewModel(profile, components);

  return (
    <div
      className="profile-public-page"
      style={{ backgroundColor: getProfilePageBackdrop(viewModel) }}
    >
      <div className="profile-public-page__frame">
        <PublicProfile
          profile={viewModel}
          onComponentClick={(comp) => {
            if (isNavigableUrl(comp.url)) {
              window.open(comp.url, '_blank', 'noopener,noreferrer');
              void recordComponentClick({ profileId: profile.id, componentId: comp.id });
            }
          }}
        />
      </div>
    </div>
  );
}
